import type { RequestLeaveItem } from "views/RequestLeave/types"

import { RequestLeaveAction, RequestLeaveStatus } from "__generated__/graphql"
import {
  CheckCircle,
  HighlightOff,
  HourglassTop,
  Logout,
  OpenInNew,
  RocketLaunch,
  SettingsRemote,
  WatchLater,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { DataViewCalendar, DetailDrawer, DialogCloseButton, getSubtleSurfaceShadow } from "components/common"
import { DateTime, Interval } from "luxon"
import { useMemo, useState } from "react"
import { getCalendarDateRange } from "utils/dateRange"
import { REQUEST_ACTION_LABELS, REQUEST_STATUS_COLORS, REQUEST_STATUS_LABELS } from "views/RequestLeave/constants"
import RequestLeaveReviewActions, { canReviewRequestLeave } from "views/RequestLeave/RequestLeaveReviewActions"
import { REQUEST_LEAVE_LAYOUT } from "views/RequestLeave/styles"
import { formatRequestLeavePeriod } from "views/RequestLeave/utils"

interface RequestLeaveCalendarProps {
  endDate: DateTime
  leaves: RequestLeaveItem[]
  onReview: (leave: RequestLeaveItem, type: "approve" | "reject") => void
  reviewerDiscordId?: string
  startDate: DateTime
}

const formatTime = (iso: string) => DateTime.fromISO(iso).toFormat("HH:mm")
const dayKey = (day: DateTime) => day.toFormat("yyyy-MM-dd")
const getActionIcon = (action: RequestLeaveAction) => {
  switch (action) {
    case RequestLeaveAction.RequestLeave:
      return <Logout fontSize="inherit" />
    case RequestLeaveAction.RequestLate:
      return <WatchLater fontSize="inherit" />
    case RequestLeaveAction.RequestLeaveEarly:
      return <RocketLaunch fontSize="inherit" />
    case RequestLeaveAction.RequestRemote:
      return <SettingsRemote fontSize="inherit" />
    default:
      return null
  }
}
const getStatusIcon = (status: RequestLeaveStatus) => {
  switch (status) {
    case RequestLeaveStatus.Approved:
      return <CheckCircle fontSize="inherit" />
    case RequestLeaveStatus.AwaitingApproval:
      return <HourglassTop fontSize="inherit" />
    case RequestLeaveStatus.Rejected:
      return <HighlightOff fontSize="inherit" />
    default:
      return null
  }
}

type LeaveItemProps = {
  leave: RequestLeaveItem
  onClick: (leave: RequestLeaveItem) => void
}

const LeaveItem = ({ leave, onClick }: LeaveItemProps) => {
  const statusColor = REQUEST_STATUS_COLORS[leave.status]
  const handleOpen = () => onClick(leave)

  return (
    <Box
      aria-label={`${leave.attendee.name ?? "Nhân viên"}: ${REQUEST_ACTION_LABELS[leave.action]}, ${
        REQUEST_STATUS_LABELS[leave.status]
      }`}
      borderRadius={1}
      display="flex"
      flexDirection="column"
      gap={0.25}
      onClick={(e) => {
        e.stopPropagation()
        handleOpen()
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        handleOpen()
      }}
      position="relative"
      px={0.75}
      py={0.75}
      role="button"
      sx={(theme) => ({
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 1,
        },
        "&:hover": {
          boxShadow: getSubtleSurfaceShadow(theme),
          transform: "translateY(-1px)",
        },
        bgcolor: alpha(
          theme.palette[statusColor].main,
          REQUEST_LEAVE_LAYOUT.statusBackgroundOpacity[theme.palette.mode],
        ),
        cursor: "pointer",
        transition: theme.transitions.create(["box-shadow", "transform"], {
          duration: theme.transitions.duration.shortest,
        }),
      })}
      tabIndex={0}
    >
      <Box
        color={`${statusColor}.main`}
        lineHeight={0}
        position="absolute"
        right={4}
        sx={(theme) => ({ fontSize: theme.typography.caption.fontSize })}
        title={REQUEST_STATUS_LABELS[leave.status]}
        top={4}
      >
        {getStatusIcon(leave.status)}
      </Box>
      <Typography
        fontWeight={700}
        lineHeight={1.2}
        noWrap
        pr={2}
        title={leave.attendee?.name ?? undefined}
        variant="caption"
      >
        {leave.attendee?.name}
      </Typography>
      <Box alignItems="center" display="flex" gap={0.5}>
        <Box
          lineHeight={0}
          sx={(theme) => ({ fontSize: theme.typography.caption.fontSize })}
          title={REQUEST_ACTION_LABELS[leave.action]}
        >
          {getActionIcon(leave.action)}
        </Box>
        <Typography lineHeight={1.2} noWrap variant="caption">
          {formatTime(leave.startDate)} - {formatTime(leave.endDate)}
        </Typography>
      </Box>
    </Box>
  )
}

const RequestLeaveCalendar = ({
  endDate,
  leaves,
  onReview,
  reviewerDiscordId,
  startDate,
}: RequestLeaveCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null)

  const [selectedLeave, setSelectedLeave] = useState<null | RequestLeaveItem>(null)

  const days = useMemo(() => getCalendarDateRange(startDate, endDate), [endDate, startDate])

  const leavesByDay = useMemo(() => {
    const map = new Map<string, RequestLeaveItem[]>()

    leaves.forEach((leave) => {
      const interval = Interval.fromDateTimes(
        DateTime.fromISO(leave.startDate).startOf("day"),
        DateTime.fromISO(leave.endDate).endOf("day"),
      )

      days.forEach((day) => {
        if (!interval.contains(day)) return
        const key = dayKey(day)
        if (!map.has(key)) map.set(key, [])
        map.get(key)!.push(leave)
      })
    })

    return map
  }, [days, leaves])

  const selectedDayLeaves = useMemo(() => {
    if (!selectedDate) return []
    return leavesByDay.get(dayKey(selectedDate)) ?? []
  }, [leavesByDay, selectedDate])

  return (
    <>
      <DataViewCalendar
        endDate={endDate}
        getDayAriaLabel={(day) => `Xem tất cả yêu cầu ngày ${day.toFormat("dd/MM/yyyy")}`}
        onDayClick={setSelectedDate}
        renderDay={(day) => {
          const dayLeaves = leavesByDay.get(dayKey(day)) ?? []
          const sortDateLeave = [...dayLeaves].sort(
            (a, b) => DateTime.fromISO(a.startDate).toMillis() - DateTime.fromISO(b.startDate).toMillis(),
          )
          const visibleDayLeaves = sortDateLeave.slice(0, 2)
          const hiddenDayLeavesCount = sortDateLeave.length - visibleDayLeaves.length

          return (
            <>
              <IconButton
                aria-label={`Xem tất cả yêu cầu ngày ${day.toFormat("dd/MM/yyyy")}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedDate(day)
                }}
                size="small"
                sx={{
                  padding: 0.25,
                  position: "absolute",
                  right: 6,
                  top: 6,
                }}
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
              <Typography fontWeight={600} variant="caption">
                {day.day}
              </Typography>

              <Stack gap={0.5}>
                {visibleDayLeaves.map((leave) => (
                  <LeaveItem key={leave.id} leave={leave} onClick={setSelectedLeave} />
                ))}
                {hiddenDayLeavesCount > 0 && (
                  <Button
                    aria-label={`Xem thêm ${hiddenDayLeavesCount} yêu cầu ngày ${day.toFormat("dd/MM/yyyy")}`}
                    onClick={() => setSelectedDate(day)}
                    size="small"
                    sx={{ alignSelf: "flex-start", py: 0 }}
                    variant="text"
                  >
                    +{hiddenDayLeavesCount} yêu cầu
                  </Button>
                )}
              </Stack>
            </>
          )
        }}
        startDate={startDate}
      />

      <DetailDrawer
        onClose={() => setSelectedDate(null)}
        open={Boolean(selectedDate)}
        subtitle={`${selectedDayLeaves.length} yêu cầu`}
        title={`Ngày ${selectedDate?.toFormat("dd/MM/yyyy") ?? ""}`}
      >
        <Stack alignItems="stretch" gap={1}>
          {selectedDayLeaves.length === 0 ? (
            <Chip color="error" label="Không có yêu cầu xin nghỉ" sx={{ alignSelf: "center" }} variant="outlined" />
          ) : (
            selectedDayLeaves.map((leave) => <LeaveItem key={leave.id} leave={leave} onClick={setSelectedLeave} />)
          )}
        </Stack>
      </DetailDrawer>

      {selectedLeave && (
        <Dialog fullWidth maxWidth="sm" onClose={() => setSelectedLeave(null)} open>
          <DialogTitle>
            Chi tiết đơn xin nghỉ
            <DialogCloseButton aria-label="Đóng" onClick={() => setSelectedLeave(null)} />
          </DialogTitle>

          <DialogContent>
            <Stack gap={1.5}>
              <Typography>
                <b>Nhân viên:</b> {selectedLeave.attendee?.name}
              </Typography>

              <Typography>
                <b>Thời gian:</b> {formatRequestLeavePeriod(selectedLeave.startDate, selectedLeave.endDate)}
              </Typography>

              <Typography>
                <b>Loại:</b> {REQUEST_ACTION_LABELS[selectedLeave.action]}
              </Typography>

              <Typography>
                <b>Trạng thái:</b> {REQUEST_STATUS_LABELS[selectedLeave.status]}
              </Typography>

              <Typography>
                <b>Lý do:</b> {selectedLeave.reason || "—"}
              </Typography>

              <Typography>
                <b>Người phê duyệt:</b> {selectedLeave.approver?.name}
              </Typography>
            </Stack>
          </DialogContent>

          {canReviewRequestLeave(selectedLeave, reviewerDiscordId) && (
            <DialogActions>
              <RequestLeaveReviewActions
                leave={selectedLeave}
                onReview={(leave, type) => {
                  onReview(leave, type)
                  setSelectedLeave(null)
                }}
                reviewerDiscordId={reviewerDiscordId}
              />
            </DialogActions>
          )}
        </Dialog>
      )}
    </>
  )
}

export default RequestLeaveCalendar
